/**
 * Bundle 2 item 4 — the Evidence trail, made readable.
 *
 * The trail used to render each `evidence_index` row by walking
 * `Object.entries` and stringifying whatever it found. Two of the row's keys
 * hold objects rather than scalars, so the audit trail read
 * `evidence=[object Object]` on *every* letter, and `review_findings=` — a bare
 * key with nothing after it — on every letter that had nothing to review. The
 * filter dropped `null` and `''` but an empty array is neither.
 *
 * This is the part of the screen a reviewer reads after the fact, when the
 * question is "what did this letter actually rely on". It has to answer that.
 *
 * **Nothing here judges anything.** `verification`, `match_status` and every
 * `reason` come off the response untouched — the same rule `verificationResult`
 * works under, for the same reason: the documents never leave the server, so a
 * verdict computed here would be a guess wearing the engine's authority. What
 * this module does is pick named fields and drop the ones the server sent for
 * machines.
 */
import type { EvidenceIndexEntry, MatchStatus } from '@/components/attest/api'

/** One attached document, as the letter cited it. */
export interface TrailSource {
  /**
   * `reference — location — obtained date`, blanks dropped.
   *
   * Deliberately the same join as `Evidence.citation()` in
   * `core/attest/letter_model.py`: this line is what the .docx's own evidence
   * list says, and a trail that cited documents differently from the document
   * it is a trail for would be its own small contradiction.
   */
  citation: string
  /** The classifier's kind, or `''` when nothing classified it. */
  kind: string
  /**
   * How the kind was decided — `content` | `filename` | `magic` | `ai_fallback`.
   * Only the first means the classifier read the document rather than its name,
   * which is exactly the distinction a reviewer is here to make.
   */
  derivation: string
  /** `gross_receipts = 1,250,000`, or `''` when no figure was pinned. */
  figure: string
  /** `entity Acme LLC · period 2024 · preparer Jane Roe`, or `''`. */
  facts: string
}

/** One statement and everything the engine recorded against it. */
export interface TrailRow {
  assertionId: string
  statement: string
  /** True when the engine passed it outright. */
  verified: boolean
  /** `VERIFIED`, or what did not line up. */
  code: MatchStatus
  sources: TrailSource[]
  /** The server's own prose, verbatim, one per review finding. */
  notes: Array<{ gate: string; reason: string }>
}

/** A named field as a display string, or `''`. Objects and arrays never pass. */
function scalar(row: Record<string, unknown>, key: string): string {
  const value = row[key]
  if (value === null || value === undefined || typeof value === 'object') return ''
  if (typeof value === 'boolean') return value ? 'yes' : ''
  return String(value).trim()
}

/** The nested `verified_facts` dict, or `null` — never rendered whole. */
function factsOf(row: Record<string, unknown>): Record<string, unknown> | null {
  const value = row.verified_facts
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

/** The three facts a reviewer checks a letter against. Order is fixed. */
const FACT_LABELS: Array<[string, string]> = [
  ['entity', 'entity'],
  ['period', 'period'],
  ['preparer_name', 'preparer'],
]

function readSource(row: Record<string, unknown>): TrailSource {
  const facts = factsOf(row)
  const figureKey = scalar(row, 'figure_key')
  const figureValue = scalar(row, 'figure_value')

  return {
    citation:
      [scalar(row, 'reference'), scalar(row, 'location'), scalar(row, 'obtained_date')]
        .filter(Boolean)
        .join(' — ')
      // A source with no reference at all should still occupy a line: it is
      // evidence the letter carried, and silently dropping it would make the
      // trail shorter than the thing it describes.
      || scalar(row, 'source_type')
      || 'Unnamed source',
    kind: scalar(row, 'document_kind'),
    derivation: scalar(row, 'classification_derivation'),
    figure: figureKey ? `${figureKey} = ${figureValue || '—'}` : '',
    facts: facts
      ? FACT_LABELS
        .map(([key, label]) => {
          const value = scalar(facts, key)
          return value ? `${label} ${value}` : ''
        })
        .filter(Boolean)
        .join(' · ')
      : '',
  }
}

/**
 * `LetterArtifact.evidence_index` as rows the trail can render.
 *
 * Findings are filtered to the ones carrying prose. A finding with no `reason`
 * is a machine record; printing its gate name alone would put a row on screen
 * that says a check ran and nothing about what it found.
 */
export function trailRows(index: EvidenceIndexEntry[] | undefined): TrailRow[] {
  return (index ?? []).map(entry => ({
    assertionId: entry.assertion_id ?? '',
    statement: entry.statement ?? '',
    verified: entry.verification !== 'review',
    code: entry.match_status ?? 'VERIFIED',
    sources: (entry.evidence ?? []).map(readSource),
    notes: (entry.review_findings ?? [])
      .filter(f => (f.reason ?? '').trim())
      .map(f => ({ gate: f.gate ?? '', reason: f.reason })),
  }))
}
